import React from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ContactPage() {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = React.useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. An advisor will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,165,233,0.16),transparent_55%)]" />
        <div className="mfb-container relative">
          <div className="overline mb-4">Contact</div>
          <h1 className="font-[Outfit] text-5xl sm:text-6xl font-light tracking-tighter text-white max-w-3xl leading-[1.05]">
            Let&rsquo;s build your <span className="gradient-text-gold">wealth strategy</span>.
          </h1>
          <p className="mt-5 max-w-xl text-slate-300 text-base">
            Drop us a note and a dedicated advisor will reach out within 24 hours.
          </p>
        </div>
      </section>

      <section className="mfb-section pt-0">
        <div className="mfb-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            {[
              { i: Mail, t: "Email", v: "support@myfundingbank.com" },
              { i: Phone, t: "Phone", v: "+1 (800) 555-MFB-1" },
              { i: MapPin, t: "HQ", v: "One Marina Towers, Level 38, Dubai" },
            ].map((c, i) => (
              <div key={i} className="card-luxury p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center">
                  <c.i className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{c.t}</div>
                  <div className="text-white text-sm mt-1">{c.v}</div>
                </div>
              </div>
            ))}
            <div className="glass rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-sky-400 mb-3">Office hours</div>
              <div className="text-slate-300 text-sm">Mon – Fri · 09:00 – 21:00 GST</div>
              <div className="text-slate-300 text-sm">Sat · 10:00 – 16:00 GST</div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 card-luxury p-7 sm:p-10 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" name="name" value={form.name} onChange={onChange} required testId="contact-name" />
              <Field label="Email" type="email" name="email" value={form.email} onChange={onChange} required testId="contact-email" />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testId="contact-phone" />
              <Field label="Subject" name="subject" value={form.subject} onChange={onChange} testId="contact-subject" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">Message</label>
              <Textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={6}
                placeholder="Tell us about your goals…"
                data-testid="contact-message"
                className="bg-[#111827] border-white/10 focus:border-sky-500 text-white placeholder:text-slate-500 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="btn-primary disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Message"} <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, ...rest }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">{label}</label>
      <Input
        {...rest}
        data-testid={testId}
        className="bg-[#111827] border-white/10 focus:border-sky-500 text-white placeholder:text-slate-500"
      />
    </div>
  );
}
